// Unsaved-changes detection for the desktop/web estimator.
//
// The old flag was "any tracked field changed → dirty". That fired on things
// that are not edits — launching, opening a file, New, the cloud draft
// arriving — because those set fields too, so the app warned about unsaved
// changes the user never made. Instead we keep a BASELINE: the fingerprint of
// what was last saved (or loaded, or started fresh), and the estimate is dirty
// exactly when what Save would write now differs from it.
//
// The fingerprint is built from the save function's own output (App's
// buildSnapshot), so a field added to the file is compared automatically.
// Only the keys below are left out, each because it changes without the user
// doing anything and is not their work.

/** Keys of the saved estimate that are NOT compared. Keep this list short and
 *  justify every entry — anything left out here can be lost without warning. */
export const NOT_COMPARED_KEYS = [
  // Timestamps stamped fresh on every save.
  "savedAt",
  "pricingSnapshotAt",
  // The company price list, refreshed from the server after launch and on
  // open. The one deliberate pricing action (accepting current prices on a
  // frozen file) forces the estimate dirty explicitly instead.
  "pricingItems",
  "pricingCategories",
  // Product photos, derived from the selections (which are compared) and the
  // visual library, which loads asynchronously.
  "visuals",
  // Built automatically from Town + Last name (both compared); there is no
  // way to type it directly.
  "estimateName",
] as const;

export type Fingerprint = {
  /** Everything compared except the contract. */
  rest: string;
  /** The contract on its own, so the Contract page's auto-fill can be
   *  absorbed without hiding a real edit elsewhere. */
  contract: string;
};

/** JSON with object keys sorted, so key order never reads as a change. */
export function stableStringify(value: unknown): string {
  return JSON.stringify(value, (_k, v) => {
    if (v && typeof v === "object" && !Array.isArray(v)) {
      const out: Record<string, unknown> = {};
      for (const k of Object.keys(v).sort()) out[k] = (v as any)[k];
      return out;
    }
    return v;
  });
}

export function estimateFingerprint(snapshot: Record<string, any>): Fingerprint {
  const rest: Record<string, any> = {};
  for (const [k, v] of Object.entries(snapshot || {})) {
    if (k === "contract") continue;
    if ((NOT_COMPARED_KEYS as readonly string[]).includes(k)) continue;
    rest[k] = v === undefined ? null : v;
  }
  return {
    rest: stableStringify(rest),
    contract: stableStringify(snapshot?.contract ?? null),
  };
}

/** The Contract page auto-fills every time its tab opens. If the contract
 *  matched the baseline just before that auto-fill, the auto-filled contract
 *  becomes the baseline (it is rebuilt from the estimate on every visit, so
 *  nothing is lost). A contract the user had already changed stays changed. */
export function absorbAutoContract(
  baseline: Fingerprint,
  contractBeforeChange: string,
  current: Fingerprint,
  changeWasAuto: boolean
): Fingerprint {
  if (!changeWasAuto) return baseline;
  if (contractBeforeChange !== baseline.contract) return baseline;
  return { ...baseline, contract: current.contract };
}

export function differsFromBaseline(
  baseline: Fingerprint,
  current: Fingerprint
): boolean {
  return baseline.rest !== current.rest || baseline.contract !== current.contract;
}

// ---------------------------------------------------------------------------
// "Is the estimate on screen safely in a file?" — decides whether opening
// another file (or New) has to ask first. isDirty alone is not enough: the
// baseline after launch is the restored cloud draft, which may never have
// been written to a file, so replacing it without asking would lose it. We
// remember the fingerprint of the last estimate written to or read from a
// file (in localStorage, so it survives a restart) and treat the estimate as
// safe only when it is clean AND its baseline is exactly that file.

export const FILE_RECORD_KEY = "du_last_file_fingerprint";

export type FileRecord = { estimateId: string } & Fingerprint;

export function makeFileRecord(estimateId: string, fp: Fingerprint): FileRecord {
  return { estimateId, rest: fp.rest, contract: fp.contract };
}

export function parseFileRecord(raw: string | null): FileRecord | null {
  if (!raw) return null;
  try {
    const r = JSON.parse(raw);
    if (
      r &&
      typeof r.estimateId === "string" &&
      typeof r.rest === "string" &&
      typeof r.contract === "string"
    ) {
      return r as FileRecord;
    }
  } catch {}
  return null;
}

/** True when the user would lose work by replacing what's on screen. */
export function hasUnsavedWork(args: {
  hasContent: boolean;
  isDirty: boolean;
  estimateId: string;
  baseline: Fingerprint | null;
  fileRecord: FileRecord | null;
}): boolean {
  const { hasContent, isDirty, estimateId, baseline, fileRecord } = args;
  if (!hasContent) return false;
  if (isDirty) return true;
  if (!baseline || !fileRecord || !estimateId) return true;
  return (
    fileRecord.estimateId !== estimateId ||
    differsFromBaseline(baseline, fileRecord)
  );
}

/** Does this saved estimate hold any scope worth protecting? Client fields
 *  alone don't count. App's hasScopeContent calls this with its live state,
 *  so the rule is the same for the screen and for a snapshot. */
export function snapshotHasScopeContent(snap: Record<string, any>): boolean {
  const s = snap || {};
  const contract = s.contract;
  const nonEmptyContract =
    !!contract &&
    (typeof contract !== "object" || Object.keys(contract).length > 0);
  return (
    !!s.constructionType ||
    !!s.selectedDeckingId ||
    s.deckingSqFt > 0 ||
    !!s.selectedRailingId ||
    s.railingLf > 0 ||
    !!s.selectedStairsId ||
    (s.stairsCount ?? 0) > 0 ||
    !!s.selectedFastenerId ||
    !!s.selectedDemoId ||
    s.demoQty > 0 ||
    !!s.skirtingCategory ||
    !!s.selectedSkirtingId ||
    s.skirtingSf > 0 ||
    !!s.miValue ||
    !!s.includePermit ||
    !!s.msrpMode ||
    (Array.isArray(s.addItems) && s.addItems.length > 0) ||
    !!String(s.sowText || "").trim() ||
    !!String(s.projectDescription || "").trim() ||
    nonEmptyContract
  );
}

/** The startup cloud draft arrived after the user had already opened a file
 *  (or started New). What's on screen is their choice and is never replaced
 *  silently. The draft is dropped without a word only when nothing is lost:
 *  it is empty, or it is exactly the last file saved/opened before launch
 *  (startupRecord). Otherwise the user is asked. */
export function lateDraftNeedsQuestion(
  draft: Record<string, any>,
  startupRecord: FileRecord | null
): boolean {
  if (!snapshotHasScopeContent(draft)) return false;
  if (
    startupRecord &&
    draft.estimateId === startupRecord.estimateId &&
    !differsFromBaseline(estimateFingerprint(draft), startupRecord)
  ) {
    return false;
  }
  return true;
}
